import { withRetry } from '../utils/canvasCompletion';

/**
 * Error carrying the server's machine-readable fields so callers can branch on
 * them (e.g. `sessionEnded` for a late joiner, `code`/`retryAfterSeconds` when
 * a finalize is attempted before the server clock allows it).
 */
export function buildApiError(resp, data, fallbackMessage) {
  const err = new Error(data?.message || fallbackMessage);
  err.status = resp?.status;
  if (data?.code) err.code = data.code;
  if (data?.sessionEnded) err.sessionEnded = true;
  if (data?.retryAfterSeconds != null) err.retryAfterSeconds = Number(data.retryAfterSeconds);
  return err;
}

/**
 * Seat assignment / stage-row creation. The endpoint is idempotent (a second call for the same
 * player returns the existing rows), so a transient failure - a dropped connection, a 429 from
 * throttling, or the 503 the server answers when the per-session assignment lock is busy - is
 * retried with jittered backoff instead of leaving the player without a seat. Definite answers
 * (400, 404, 410 session ended, ...) are not retried.
 */
export function initStageReport(args) {
  return withRetry(() => initStageReportOnce(args), { maxAttempts: 4, backoffMs: 500 });
}

async function initStageReportOnce({
  token,
  userId,
  email,
  name,
  userType,
  reportId,
  themeName,
  totalPuzzles,
  currentTheme,
  stageToggles,
}) {
  const tokenStr = String(token ?? '').trim();
  const [sessionIdFromToken, organizationIdFromToken] = tokenStr.includes('&')
    ? tokenStr.split('&')
    : [null, null];
  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/initStageReport`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      userId,
      email,
      name,
      user_type: userType,
      reportid: reportId,
      themeName,
      ...(totalPuzzles != null && Number.isFinite(Number(totalPuzzles))
        ? { total_puzzles: Number(totalPuzzles) }
        : {}),
      // Lets the backend look up the theme's own saved piece count instead of
      // trusting whatever total_puzzles this particular browser has cached —
      // two players joining around the same time as an admin change could
      // otherwise each get checked against a different capacity limit.
      ...(currentTheme ? { currentTheme: String(currentTheme) } : {}),
      ...(stageToggles && typeof stageToggles === 'object' ? stageToggles : {}),
      ...(sessionIdFromToken ? { sessionId: String(sessionIdFromToken) } : {}),
      ...(organizationIdFromToken ? { organizationId: String(organizationIdFromToken) } : {}),
    }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || !data.success) {
    throw buildApiError(resp, data, 'Failed to initialize stage report');
  }
  return data;
}

export async function updateStageProgress({
  token,
  userId,
  sessionId: sessionIdParam,
  organizationId: organizationIdParam,
  dayNumber,
  points,
  timeSeconds,
  answer,
  allCorrect,
  email,
  name,
  status,
}) {
  const tokenStr = String(token ?? '').trim();
  const [sessionIdFromToken, organizationIdFromToken] = tokenStr.includes('&')
    ? tokenStr.split('&')
    : [null, null];
  const sessionId = sessionIdParam ?? sessionIdFromToken;
  const organizationId = organizationIdParam ?? organizationIdFromToken;

  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/updateStageProgress`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      userId,
      ...(sessionId ? { sessionId: String(sessionId) } : {}),
      ...(organizationId ? { organizationId: String(organizationId) } : {}),
      ...(dayNumber != null ? { dayNumber } : {}),
      points,
      timeSeconds,
      answer,
      allCorrect,
      email,
      name,
      ...(status != null ? { status } : {}),
    }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || !data.success) {
    throw buildApiError(resp, data, 'Failed to update stage progress');
  }
  return data;
}

export async function uploadStageAnswerImage({
  token,
  userId,
  sessionId,
  organizationId,
  stageNumber,
  file,
}) {
  const tokenStr = String(token ?? '').trim();
  const [sessionIdFromToken, organizationIdFromToken] = tokenStr.includes('&')
    ? tokenStr.split('&')
    : [null, null];
  const resolvedSessionId = sessionId ?? sessionIdFromToken;
  const resolvedOrganizationId = organizationId ?? organizationIdFromToken;

  const formData = new FormData();
  formData.append('image', file);
  formData.append('userId', String(userId ?? ''));
  formData.append('stageNumber', String(stageNumber ?? ''));
  if (resolvedSessionId) formData.append('sessionId', String(resolvedSessionId));
  if (resolvedOrganizationId) formData.append('organizationId', String(resolvedOrganizationId));

  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/uploadStageAnswerImage`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || !data.success) {
    throw new Error(data.message || 'Failed to upload stage image');
  }
  return data;
}

export async function submitCanvasTile({ token, userId, sessionId, organizationId, file }) {
  const tokenStr = String(token ?? '').trim();
  const [sessionIdFromToken, organizationIdFromToken] = tokenStr.includes('&')
    ? tokenStr.split('&')
    : [null, null];
  const resolvedSessionId = sessionId ?? sessionIdFromToken;
  const resolvedOrganizationId = organizationId ?? organizationIdFromToken;

  const formData = new FormData();
  formData.append('image', file);
  formData.append('userId', String(userId ?? ''));
  if (resolvedSessionId) formData.append('sessionId', String(resolvedSessionId));
  if (resolvedOrganizationId) formData.append('organizationId', String(resolvedOrganizationId));

  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/submitCanvasTile`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || !data.success) {
    const error = new Error(data.message || 'Failed to submit canvas tile');
    error.status = resp.status;
    error.code = data.code;
    // 409 = the server already holds a final tile for this player (submitted, or
    // auto-filled when the timer ended). Not a failure the player can act on.
    error.tileFinalized = resp.status === 409 && /finalized/i.test(String(data.message || ''));
    throw error;
  }
  return data;
}

/**
 * Live mode: stamps (first caller) / reads the SERVER-side drawing clock. The
 * returned startedAt/serverNow are server milliseconds; the caller converts to
 * its own clock with skew = serverNow - Date.now().
 */
export async function startCanvasActivity({ token, userId, organizationId, sessionId, currentTheme }) {
  const tokenStr = String(token ?? '').trim();
  const [sessionIdFromToken, organizationIdFromToken] = tokenStr.includes('&')
    ? tokenStr.split('&')
    : [null, null];
  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/startCanvasActivity`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      userId,
      organizationId: organizationId ?? organizationIdFromToken,
      sessionId: sessionId ?? sessionIdFromToken,
      currentTheme,
    }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || !data.success) {
    throw buildApiError(resp, data, 'Failed to start canvas activity');
  }
  return data;
}

export async function endCanvasActivity({
  token,
  userId,
  organizationId,
  sessionId,
  currentTheme,
  presentUserIds,
}) {
  const tokenStr = String(token ?? '').trim();
  const [sessionIdFromToken, organizationIdFromToken] = tokenStr.includes('&')
    ? tokenStr.split('&')
    : [null, null];
  const resolvedSessionId = sessionId ?? sessionIdFromToken;
  const resolvedOrganizationId = organizationId ?? organizationIdFromToken;

  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/endCanvasActivity`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      organizationId: resolvedOrganizationId,
      sessionId: resolvedSessionId,
      currentTheme,
      // Identifies the caller as a session participant; the server decides
      // whether the activity may be finalized from its OWN clock.
      ...(userId ? { userId: String(userId) } : {}),
      ...(Array.isArray(presentUserIds) ? { presentUserIds } : {}),
    }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || !data.success) {
    throw buildApiError(resp, data, 'Failed to end activity');
  }
  return data;
}

export async function getCanvasTiles({ token, organizationId, sessionId }) {
  const tokenStr = String(token ?? '').trim();
  const [sessionIdFromToken, organizationIdFromToken] = tokenStr.includes('&')
    ? tokenStr.split('&')
    : [null, null];
  const resolvedSessionId = sessionId ?? sessionIdFromToken;
  const resolvedOrganizationId = organizationId ?? organizationIdFromToken;

  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/getCanvasTiles`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      organizationId: resolvedOrganizationId,
      sessionId: resolvedSessionId,
    }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || !data.success) {
    throw new Error(data.message || 'Failed to fetch canvas tiles');
  }
  return data;
}

export async function fetchGameStages({ token, userId, organizationId, sessionId }) {
  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/getGameStages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ userId, organizationId, sessionId }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || !data.success) {
    throw new Error(data.message || 'Failed to fetch game stages');
  }
  return data;
}

export async function updateStageFeedback({ token, userId, dayNumber, feedback, stageId }) {
  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/updateStageFeedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      userId,
      dayNumber,
      feedback,
      stageId,
    }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || !data.success) {
    throw new Error(data.message || 'Failed to update stage feedback');
  }
  return data;
}
